import { useEffect, useRef, useCallback } from 'react';
import { useAnalytics } from './useAnalytics';

interface UseSessionTrackingProps {
  workspaceId: string;
  userId?: string;
  isPreviewMode?: boolean;
}

export const useSessionTracking = ({ workspaceId, userId, isPreviewMode }: UseSessionTrackingProps) => {
  const { trackEvent } = useAnalytics();
  const sessionStarted = useRef(false);
  const sessionStartTime = useRef<number | null>(null);
  const sessionId = useRef(generateSessionId());
  const pageViewCount = useRef<number>(0);
  const messageCount = useRef<number>(0);

  // Fonction pour générer un ID de session unique
  function generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Démarrer une session
  const startSession = useCallback(() => {
    if (workspaceId && !sessionStarted.current && !isPreviewMode) {
      sessionStarted.current = true;
      sessionStartTime.current = Date.now();
      
      trackEvent({
        workspaceId,
        type: 'session_start',
        sessionId: sessionId.current,
        metadata: {
          userAgent: navigator.userAgent,
          sessionStartTime: sessionStartTime.current,
        },
      });
    }
  }, [workspaceId, trackEvent, isPreviewMode]);

  // Terminer une session
  const endSession = useCallback(() => {
    if (sessionStarted.current && sessionStartTime.current && !isPreviewMode) {
      const duration = Math.round((Date.now() - sessionStartTime.current) / 1000);
      
      trackEvent({
        workspaceId: workspaceId!,
        type: 'session_end',
        sessionId: sessionId.current,
        metadata: {
          duration,
          sessionEndTime: Date.now(),
        },
      });

      // Reset
      sessionStarted.current = false;
      sessionStartTime.current = null;
      sessionId.current = generateSessionId();
    }
  }, [workspaceId, trackEvent, isPreviewMode]);

  // 📈 INCRÉMENTER LES COMPTEURS
  const incrementPageView = useCallback(() => {
    pageViewCount.current += 1;
  }, []);

  const incrementMessage = useCallback(() => {
    messageCount.current += 1;
  }, []);

  // 🎯 DÉMARRER LA SESSION AU MONTAGE
  useEffect(() => {
    startSession();

    // 🚪 TERMINER LA SESSION QUAND L'UTILISATEUR QUITTE
    const handleBeforeUnload = () => {
      endSession();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        endSession();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      endSession(); // Session cleanup au démontage
    };
  }, [startSession, endSession]);

  return {
    incrementPageView,
    incrementMessage,
    endSession
  };
};